import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Search, Check, Clock, Sparkles } from 'lucide-react';
import VendorIcon from './VendorIcon';
import { getRecentModels, pushRecentModel } from '../utils/recentModels';

/**
 * ModelPicker — one searchable surface that filters and suggests at the same time.
 * Replaces the old Provider dropdown + native <select> of ~300 models + filter box +
 * Haiku/Sonnet/Opus quick-pick row. Recents (localStorage) + Suggested (family latest,
 * Bedrock only) sit at the top; typing filters the full catalog live.
 *
 * Props:
 *   provider, onProviderChange(provider)
 *   models: AvailableModelDto[] for the current provider
 *   loading: catalog is loading
 *   selectedModelId, onSelect(model)
 *   disableOpenRouter: tool-calling purpose — OpenRouter unavailable (backend 400s)
 *   suggestions: [{ label, model }] | null
 */

const fmtPrice = (p) => (p == null ? '' : p === 0 ? 'Free' : p < 1 ? `$${p.toFixed(3)}` : `$${p.toFixed(2)}`);
const priceText = (m) => {
  const i = m?.inputPricePerMillionTokens, o = m?.outputPricePerMillionTokens;
  if (!i && !o) return '';
  return `${fmtPrice(i)} / ${fmtPrice(o)}`;
};
const label = (m) => m?.modelName || m?.modelId || '';

const Wrap = styled.div`
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 10px;
  background: ${(p) => p.theme.colors.backgroundSecondary};
  overflow: hidden;
`;

const Segmented = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

const Seg = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 7px;
  border: 1px solid ${(p) => (p.$active ? p.theme.colors.primary : 'transparent')};
  background: ${(p) => (p.$active ? p.theme.colors.primary + '12' : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.colors.primary : p.theme.colors.muted)};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.disabled ? 0.45 : 1)};
  transition: all 0.15s;
  &:hover:not(:disabled) { color: ${(p) => p.theme.colors.primary}; }
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.muted};
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: ${(p) => p.theme.colors.text};
  font-size: 13px;
`;

const List = styled.div`
  max-height: 320px;
  overflow-y: auto;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.muted};
`;

const Row = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: none;
  border-left: 2px solid ${(p) => (p.$active ? p.theme.colors.tint : 'transparent')};
  background: ${(p) => (p.$active ? p.theme.colors.background : 'transparent')};
  cursor: pointer;
  text-align: left;
  &:hover { background: ${(p) => p.theme.colors.background}; }
`;

const RowText = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowSub = styled.div`
  font-size: 11px;
  font-family: monospace;
  color: ${(p) => p.theme.colors.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowPrice = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme.colors.muted};
  white-space: nowrap;
`;

const ToolsBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 2px 5px;
  border-radius: 4px;
  color: ${(p) => p.theme.colors.primary};
  background: ${(p) => p.theme.colors.primary}12;
  white-space: nowrap;
  flex: 0 0 auto;
`;

const Hint = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 11px;
  color: ${(p) => p.theme.colors.muted};
  background: ${(p) => p.theme.colors.background};
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

const CheckIcon = styled(Check)`
  color: ${(p) => p.theme.colors.tint};
  flex: 0 0 auto;
`;

const Note = styled.div`
  padding: 14px 12px;
  font-size: 12px;
  color: ${(p) => p.theme.colors.muted};
  text-align: center;
`;

const ModelRow = ({ model, active, selected, onClick, onMouseEnter }) => (
  <Row $active={active} onClick={onClick} onMouseEnter={onMouseEnter} type="button">
    <VendorIcon vendor={model.vendorName || model.modelId} size={22} />
    <RowText>
      <RowName>{label(model)}</RowName>
      <RowSub title={model.modelId}>{model.modelId}</RowSub>
    </RowText>
    {model.supportsTools && <ToolsBadge title="Supports function/tool calling">Tools</ToolsBadge>}
    {priceText(model) && <RowPrice>{priceText(model)}</RowPrice>}
    {selected && <CheckIcon size={16} />}
  </Row>
);

const ModelPicker = ({
  provider,
  onProviderChange,
  models = [],
  loading = false,
  selectedModelId,
  onSelect,
  disableOpenRouter = false,
  suggestions = null,
  filterHint = null,
}) => {
  const [query, setQuery] = useState('');
  const [recents, setRecents] = useState(() => getRecentModels(provider));
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    setRecents(getRecentModels(provider));
    setQuery('');
  }, [provider]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return models;
    return models.filter(
      (m) =>
        m.modelId?.toLowerCase().includes(q) ||
        m.modelName?.toLowerCase().includes(q) ||
        m.vendorName?.toLowerCase().includes(q)
    );
  }, [models, query]);

  const searching = query.trim().length > 0;
  const recentRows = searching ? [] : recents;
  const suggestRows = searching || !suggestions ? [] : suggestions.map((s) => s.model).filter(Boolean);
  // Flattened, in render order, for keyboard nav.
  const flat = useMemo(
    () => [...recentRows, ...suggestRows, ...filtered],
    [recentRows, suggestRows, filtered]
  );

  useEffect(() => { setActiveIndex(0); }, [query, provider, models]);

  const choose = (model) => {
    if (!model?.modelId) return;
    setRecents(pushRecentModel(provider, model));
    onSelect(model);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flat[activeIndex]) choose(flat[activeIndex]);
    }
  };

  // Keep the keyboard-active row in view.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  let idx = -1;
  const renderRow = (model, keyPrefix) => {
    idx += 1;
    const myIdx = idx;
    return (
      <span data-idx={myIdx} key={`${keyPrefix}:${model.modelId}`}>
        <ModelRow
          model={model}
          active={myIdx === activeIndex}
          selected={model.modelId === selectedModelId}
          onClick={() => choose(model)}
          onMouseEnter={() => setActiveIndex(myIdx)}
        />
      </span>
    );
  };

  return (
    <Wrap>
      <Segmented>
        <Seg $active={provider === 'Bedrock'} onClick={() => onProviderChange('Bedrock')} type="button">
          Bedrock
        </Seg>
        <Seg
          $active={provider === 'OpenRouter'}
          disabled={disableOpenRouter}
          title={disableOpenRouter ? 'Requires Bedrock — this purpose uses tool-calling' : undefined}
          onClick={() => !disableOpenRouter && onProviderChange('OpenRouter')}
          type="button"
        >
          OpenRouter
        </Seg>
      </Segmented>

      <SearchRow>
        <Search size={15} />
        <SearchInput
          autoFocus
          placeholder={`Search ${provider} models…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
      </SearchRow>

      {filterHint && (
        <Hint><Sparkles size={11} /> {filterHint}</Hint>
      )}

      <List ref={listRef}>
        {loading ? (
          <Note>Loading {provider} catalog…</Note>
        ) : flat.length === 0 ? (
          <Note>{models.length === 0 ? 'No models available.' : 'No models match your search.'}</Note>
        ) : (
          <>
            {recentRows.length > 0 && (
              <>
                <SectionLabel><Clock size={11} /> Recent</SectionLabel>
                {recentRows.map((m) => renderRow(m, 'recent'))}
              </>
            )}
            {suggestRows.length > 0 && (
              <>
                <SectionLabel><Sparkles size={11} /> Suggested</SectionLabel>
                {suggestRows.map((m) => renderRow(m, 'suggest'))}
              </>
            )}
            {filtered.length > 0 && (
              <>
                {!searching && (recentRows.length > 0 || suggestRows.length > 0) && (
                  <SectionLabel>All models</SectionLabel>
                )}
                {filtered.map((m) => renderRow(m, 'all'))}
              </>
            )}
          </>
        )}
      </List>
    </Wrap>
  );
};

export default ModelPicker;
